import { FC, ReactNode } from "react";
import { Restricted } from "../../../components/Restricted";
import { PureVoidFunc } from "../../../helpers/functions/types";
import { capitalizeAssociatedPress } from "../../../helpers/string/capitalize";
import { DIVIDED_STYLES, GROUP_STYLES } from "../detailedPages/styles";
import { NoDataMessage } from "../detailedPages/NoDataMessage";
import { Button } from "./Button";
import { UserMinus } from "lucide-react";
import { DEFAULT_ICON_COLORS, LARGE_ICON_SIZE_STYLES } from "../../styles";

const TABLE_CLASSES =
  "first:px-4 first:max-w-[120px] first:sm:max-w-[200px] text-left text-sm text-primary py-3.5 truncate whitespace-nowrap text-stone-700 dark:text-stone-300";

export type TableData = {
  cells: (string | ReactNode)[];
  action: PureVoidFunc;
  permissions: string;
  upgradeAction?: {
    label: string;
    onClick: PureVoidFunc;
    permissions: string;
  };
};
export const RoleTable: FC<{
  thData?: string[];
  tbData?: TableData[];
  editable?: boolean;
  disabled?: boolean;
  fallbackMessage: string;
}> = ({ thData, tbData, editable = true, disabled, fallbackMessage }) => {
  return (
    <div className="overflow-x-auto rounded md:ml-0 md:mr-0 md:rounded-lg">
      {tbData?.length ? (
        <table className={`w-full rounded md:rounded-lg ${DIVIDED_STYLES}`}>
          {thData && (
            <thead className="bg-stone-100 dark:bg-stone-700">
              <tr>
                {thData.map((data, index) => (
                  <th
                    scope="col"
                    className={`${TABLE_CLASSES} font-medium`}
                    key={`header${index}`}
                  >
                    {capitalizeAssociatedPress(data)}
                  </th>
                ))}
                {editable && (
                  <th scope="col" className={TABLE_CLASSES}>
                    <span className="sr-only">More options</span>
                  </th>
                )}
              </tr>
            </thead>
          )}
          <tbody className={`bg-stone-50 dark:bg-stone-800 ${DIVIDED_STYLES}`}>
            {tbData.map(
              ({ cells, action, permissions, upgradeAction }, index) => (
                <tr key={`row${index}`}>
                  {cells.map((cell, cellIndex) => (
                    <td
                      key={`cell${index} ${cellIndex}`}
                      className={TABLE_CLASSES}
                    >
                      {cell}
                    </td>
                  ))}
                  {editable && (
                    <td className="py-4 pl-3 pr-4 text-sm font-medium sm:pr-4">
                      <div className="flex items-center justify-end gap-2">
                        {upgradeAction && (
                          <Restricted permissions={upgradeAction.permissions}>
                            <Button
                              title={upgradeAction.label}
                              onClick={upgradeAction.onClick}
                              disabled={disabled}
                              buttonType="secondary"
                            />
                          </Restricted>
                        )}
                        <Restricted permissions={permissions}>
                          <Button
                            title="delete role"
                            onClick={action}
                            tooltip="delete role"
                            disabled={disabled}
                            className={`${LARGE_ICON_SIZE_STYLES} ${DEFAULT_ICON_COLORS}`}
                            buttonType="iconOnly"
                          >
                            <UserMinus />
                          </Button>
                        </Restricted>
                      </div>
                    </td>
                  )}
                </tr>
              ),
            )}
          </tbody>
        </table>
      ) : (
        <div className={GROUP_STYLES}>
          <NoDataMessage>{fallbackMessage}</NoDataMessage>
        </div>
      )}
    </div>
  );
};
